import {
    Component,
    h,
    Host,
    State,
    Prop,
    Element,
    Listen,
    Event,
    EventEmitter,
    Method,
    Watch,
} from "@stencil/core";
import { debounce } from "../../../utils/debounce";
import { getViewportWidth } from "../../../utils/getViewportWidth";
import { throttle } from "../../../utils/throttle";

import { whiteCircle } from "./svgs/white-circle.js";
import { greenCircle } from "./svgs/green-circle.js";

@Component({
    tag: "ux-iws-interactive-tile",
    styleUrl: "interactive-tile.scss",
})
export class InteractiveTiles {
    private baseClass = "ux-iws-interactive-tile";
    private contentWrapper: HTMLDivElement;
    private content: HTMLDivElement;
    private tileIcon: HTMLUxIconElement;

    /** ids for the circle icon gradients */
    private idIconGradient1: number;
    private idIconGradient2: number;

    @Element() element: HTMLUxIwsInteractiveTileElement;

    /** if set, the content of the tiles will be interactive */
    @Prop() interactive: boolean = false;
    /** if set, the tile will display an icon */
    @Prop() iconName: string;
    /** if set, the tile will display a number */
    @Prop() number: string;
    /** if set, the tile will be active */
    @Prop({ mutable: true }) active: boolean = false;

    @State() isSelected: boolean = false;
    @State() isHovered: boolean = false;
    @State() contentHeight: number = 0;
    @State() viewportWidth: number = 0;
    @State() minimumTileHeight: number = 0;

    connectedCallback() {
        this.idIconGradient1 = ++gradientIdCounter;
        this.idIconGradient2 = ++gradientIdCounter;
    }

    /** method used from the parent component to get the height of the content element */
    @Method()
    async getContentHeight() {
        return this.contentHeight;
    }

    /** method used from the parent component to get the height of the content wrapper element */
    @Method()
    async getContentWrapperHeight() {
        let height: number = 0;
        if (this.iconName && this.tileIcon.scrollHeight !== 65) {
            height = this.contentWrapper.getBoundingClientRect().height + 65;
        } else height = this.contentWrapper.getBoundingClientRect().height;
        return height;
    }

    componentWillLoad() {
        this.viewportWidth = getViewportWidth();
    }

    componentDidLoad() {
        this.setContentHeight();
    }

    @Listen("resize", { target: "window" })
    handleResize() {
        this.onResize();
    }

    @Listen("uxInteractiveTileSelected", { target: "window" })
    handleTileSelectedEvent(e: CustomEvent) {
        const eTarget = e.target as HTMLElement;
        if (eTarget.parentElement !== this.element.parentElement) return;
        if (this.element === eTarget) return;
        this.isSelected = false;
    }

    @Event({
        eventName: "uxInteractiveTileSelected",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    interactiveTileSelected: EventEmitter;

    @Event({
        eventName: "uxInteractiveTileHeight",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    interactiveTileHeight: EventEmitter;

    private onResize = debounce(() => {
        this.resetContentHeight();
        this.viewportWidth = getViewportWidth();
    }, 200);

    private resetContentHeight = () => {
        this.contentHeight = null;
    };

    private setContentHeight = () => {
        if (this.viewportWidth < 1024)
            this.contentHeight = this.content.scrollHeight;
        else {
            let contentElementMaxHeight: number = 0;
            const contentElements = this.element.parentElement.querySelectorAll(
                `.${this.baseClass}__content`
            );
            contentElements.forEach((contentElement: HTMLElement) => {
                const contentElementHeight = contentElement.scrollHeight;
                if (contentElementHeight > contentElementMaxHeight)
                    contentElementMaxHeight = contentElementHeight;
            });
            this.contentHeight = contentElementMaxHeight;
        }
    };

    private handleMouseEnter = throttle(() => {
        this.isHovered = true;
    }, 500);

    private handleMouseLeave = () => (this.isHovered = false);

    private handleSelected = () => {
        this.isSelected = !this.isSelected;
        this.interactiveTileSelected.emit();
    };

    @Watch("isSelected")
    @Watch("isHovered")
    setActiveState() {
        if (this.isSelected || this.isHovered) this.active = true;
        else this.active = false;
    }

    @Watch("contentHeight")
    adjustContentHeight() {
        if (this.contentHeight === null)
            setTimeout(() => this.setContentHeight());
    }

    render() {
        const isActive = this.isHovered || this.isSelected;

        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--interactive`]: this.interactive,
                    [`${this.baseClass}--active`]: isActive,
                    [`${this.baseClass}--with-number`]: !!this.number,
                }}
                onMouseEnter={this.interactive && this.handleMouseEnter}
                onMouseLeave={this.interactive && this.handleMouseLeave}
                onClick={this.interactive && this.handleSelected}
                onKeyDown={this.interactive && this.handleSelected}
                tabindex={this.interactive ? "0" : null}
            >
                <div
                    class={{
                        [`${this.baseClass}__content-wrapper`]: true,
                        [`${this.baseClass}__content-wrapper--static`]: !this
                            .interactive,
                    }}
                    ref={(el) => (this.contentWrapper = el)}
                >
                    <div class={`${this.baseClass}__header`}>
                        {this.iconName && this.iconName !== "" && (
                            <div class={`${this.baseClass}__icon-container`}>
                                <ux-icon
                                    ref={(el) => (this.tileIcon = el)}
                                    name={this.iconName}
                                ></ux-icon>
                            </div>
                        )}
                        {this.number && this.number !== "" && (
                            <div class={`${this.baseClass}__number-container`}>
                                <span
                                    class={{
                                        [`${this.baseClass}__number`]: true,
                                        [`${this.baseClass}__number--green`]:
                                            !this.active && this.interactive,
                                    }}
                                >
                                    {this.number}
                                </span>
                                <div
                                    class={`${this.baseClass}__number-circle`}
                                    innerHTML={
                                        this.active || !this.interactive
                                            ? whiteCircle(this.idIconGradient1)
                                            : greenCircle(this.idIconGradient2)
                                    }
                                ></div>
                            </div>
                        )}
                        <slot name="headline"></slot>
                    </div>
                    <div
                        class={{
                            [`${this.baseClass}__content`]: true,
                            [`${this.baseClass}__content--interactive`]: this
                                .interactive,
                            [`${this.baseClass}__content--active`]: isActive,
                        }}
                        style={
                            this.interactive
                                ? {
                                      height: isActive
                                          ? `${this.contentHeight}px`
                                          : `0px`,
                                  }
                                : null
                        }
                        ref={(el) => (this.content = el)}
                        aria-hidden={
                            this.interactive && !isActive ? "true" : null
                        }
                    >
                        <div class="ux-text-normal" data-same-height="text">
                            <slot name="copy-text"></slot>
                        </div>
                        <slot name="link"></slot>
                    </div>
                </div>
                {this.interactive && !isActive && (
                    <div class={`${this.baseClass}__up-arrow`}>
                        <ux-icon name="chevron-down"></ux-icon>
                    </div>
                )}
            </Host>
        );
    }
}

/** this variable is incremented by each instance */
let gradientIdCounter = 0;
